import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  Button,
  Badge,
  Modal,
  TextInput,
  Select,
  Textarea,
  Title,
  Text,
  Group,
  Stack,
  Breadcrumbs,
  ThemeIcon,
  SimpleGrid,
  Loader,
  Paper,
  Center,
  Tabs,
  Divider,
  Box,
  RingProgress,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import {
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
  IconPrinter,
  IconServer,
  IconUser,
  IconArrowLeft,
  IconUserPlus,
  IconSearch,
  IconInfoCircle,
  IconDownload,
  IconFileText,
  IconDrone,
  IconCheck,
  IconX,
  IconBarcode,
  IconSettings,
  IconHistory,
  IconCalendar,
  IconTrophy,
  IconNote,
  IconChevronRight,
  IconBox,
  IconPackage,
  IconClipboardList,
} from '@tabler/icons-react';
import { api } from '../../api/CheckApi';

const DeviceList = () => {
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);

  const [deviceTypes, setDeviceTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [devices, setDevices] = useState([]);

  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('types');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceModalInfo, setDeviceModalInfo] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState({
    employee_id: '',
    notes: '',
  });

  useEffect(() => {
    const role = (localStorage.getItem('user_role') || '').toLowerCase();
    const adminCheck =
      role === 'admin' ||
      role === 'yönetici' ||
      role === 'it' ||
      role === 'bt' ||
      role === 'bilgi işlem' ||
      role === 'bilgi_islem';

    setIsAdmin(adminCheck);

    loadDeviceTypes();
      loadEmployees();
  }, []);

  const buildEmployeeLabel = (employee) => {
    const name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
    const area =
      employee.employee_type === 'SITE'
        ? employee.site_name || 'Şantiye'
        : employee.department || 'Merkez Ofis';

    return `${name} - ${area}`;
  };

  const normalizeAssignee = (assignmentData) => {
    if (!assignmentData) return null;

    if (assignmentData.assignee) {
      return assignmentData.assignee;
    }

    if (assignmentData.employee) {
      const emp = assignmentData.employee;
      return {
        id: emp.id,
        name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        department: emp.department || null,
        company: emp.site_name || null,
        email: emp.email || null,
        phone: emp.phone || null,
        employee_type: emp.employee_type || null,
      };
    }

    return null;
  };

  const normalizeAssignmentResponse = (data) => {
    if (!data) {
      return { assigned: false };
    }

    if (typeof data.assigned === 'boolean') {
      return {
        ...data,
        assignee: normalizeAssignee(data),
      };
    }

    if (data.employee || data.assignment_id || data.device_id) {
      return {
        assigned: !!data.employee,
        assignment_id: data.assignment_id || data.id || null,
        assigned_date: data.assigned_date || data.created_at || null,
        assignee: normalizeAssignee(data),
      };
    }

    return { assigned: false };
  };

  const resetAssignmentState = () => {
    setShowAssignForm(false);
    setAssignForm({
      employee_id: '',
      notes: '',
    });
  };

const loadEmployees = async () => {
  try {
    const employeesData = await api.getActiveEmployeesForAssignment();

    const formattedEmployees = (employeesData || []).map((emp) => ({
      value: String(emp.id),
      label: `${emp.first_name || ''} ${emp.last_name || ''} ${
        emp.employee_type === 'SITE'
          ? `- ${emp.site_name || 'Şantiye'}`
          : `- ${emp.department || 'Merkez Ofis'}`
      }`,
    }));

    setEmployees(formattedEmployees);
  } catch (err) {
    console.error('Personel listesi yüklenemedi:', err);
    notifications.show({
      title: 'Hata',
      message: 'Personel listesi yüklenemedi.',
      color: 'red',
    });
    setEmployees([]);
  }
};

  const searchDevices = async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const allDevices = await api.getDevices();
      const filtered = allDevices.filter((device) => {
        const query = term.toLowerCase();
        const serialMatch = device.serial_number?.toLowerCase().includes(query);
        const barcodeMatch = device.barcode?.toLowerCase().includes(query);
        const brandMatch = (device.model?.brand?.name || '').toLowerCase().includes(query);
        const modelMatch = (device.model?.name || '').toLowerCase().includes(query);
        return serialMatch || barcodeMatch || brandMatch || modelMatch;
      });
      setSearchResults(filtered);
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Arama sırasında hata oluştu',
        color: 'red',
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.currentTarget.value;
    setSearchTerm(value);

    if (window.searchTimeout) clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => searchDevices(value), 300);
  };

  const loadDeviceTypes = async () => {
    setLoading(true);
    try {
      const data = await api.getDeviceTypes();
      setDeviceTypes(data || []);
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Cihaz türleri yüklenemedi',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async (deviceTypeId) => {
    setLoading(true);
    setBrands([]);
    try {
      const data = await api.getBrandsByDeviceType(deviceTypeId);
      setBrands(data || []);
      setCurrentView('brands');
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Markalar yüklenemedi',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async (brandId, deviceTypeId) => {
    setLoading(true);
    setModels([]);
    try {
      const data = await api.getModelsByBrand(brandId, deviceTypeId);
      setModels(data || []);
      setCurrentView('models');
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Modeller yüklenemedi',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async (modelId) => {
    setLoading(true);
    try {
      const data = await api.getDevices(modelId);
      setDevices(data || []);
      setCurrentView('devices');
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Cihazlar yüklenemedi',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentDeviceList = async () => {
    if (currentView === 'devices' && selectedModel) {
      await loadDevices(selectedModel.id);
    }

    if (searchTerm.trim()) {
      await searchDevices(searchTerm);
    }
  };

  const handleTypeClick = (deviceType) => {
    setSelectedType(deviceType);
    setSelectedBrand(null);
    setSelectedModel(null);
    loadBrands(deviceType.id);
  };

  const handleBrandClick = (brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    loadModels(brand.id, selectedType.id);
  };

  const handleModelClick = (model) => {
    setSelectedModel(model);
    loadDevices(model.id);
  };

  const goBack = () => {
    if (currentView === 'devices') {
      setCurrentView('models');
      setSelectedModel(null);
    } else if (currentView === 'models') {
      setCurrentView('brands');
      setSelectedBrand(null);
    } else if (currentView === 'brands') {
      setCurrentView('types');
      setSelectedType(null);
      setSelectedBrand(null);
      setSelectedModel(null);
    }
  };

  const getDeviceIcon = (typeName, size = 28) => {
    const type = typeName?.toLowerCase() || '';
    if (type.includes('laptop')) return <IconDeviceDesktop size={size} stroke={1.7} />;
    if (type.includes('masaüstü') || type.includes('desktop')) return <IconServer size={size} stroke={1.7} />;
    if (type.includes('monitör') || type.includes('monitor')) return <IconDeviceDesktop size={size} stroke={1.7} />;
    if (type.includes('tablet')) return <IconDeviceTablet size={size} stroke={1.7} />;
    if (type.includes('yazıcı') || type.includes('printer')) return <IconPrinter size={size} stroke={1.7} />;
    if (type.includes('drone')) return <IconDrone size={size} stroke={1.7} />;
    if (type.includes('telefon') || type.includes('phone')) return <IconDeviceMobile size={size} stroke={1.7} />;
    return <IconDeviceDesktop size={size} stroke={1.7} />;
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === 'müsait') return <Badge color="teal" variant="light">Müsait</Badge>;
    if (s === 'atanmış' || s === 'zimmetli') return <Badge color="orange" variant="light">Atanmış</Badge>;
    if (s === 'bakım' || s === 'bakımda' || s === 'arıza') return <Badge color="red" variant="light">Bakımda</Badge>;
    if (s === 'emekli' || s === 'hurda') return <Badge color="gray" variant="light">Emekli</Badge>;
    return <Badge color="gray" variant="light">{status || 'Bilinmiyor'}</Badge>;
  };

  const getConditionColor = (condition) => {
    const c = condition?.toLowerCase();
    if (c === 'mükemmel') return 'green';
    if (c === 'iyi') return 'blue';
    if (c === 'orta') return 'yellow';
    if (c === 'zayıf') return 'orange';
    if (c === 'kötü') return 'red';
    return 'gray';
  };

  const fetchAndSetAssignment = async (deviceId) => {
    try {
      const assignmentData = await api.getDeviceAssignment(deviceId);
      return normalizeAssignmentResponse(assignmentData);
    } catch {
      return { assigned: false, message: 'Atama bilgisi yüklenemedi' };
    }
  };

  const handleDeviceCardClick = async (device) => {
    setAssignmentLoading(true);
    setShowDeviceModal(true);
    resetAssignmentState();

    try {
      const [fullDetail, assignmentData] = await Promise.all([
        api.getDeviceDetail(device.id),
        fetchAndSetAssignment(device.id),
      ]);

      setDeviceModalInfo({
        ...fullDetail,
        assignmentInfo: assignmentData,
      });
    } catch (err) {
      setDeviceModalInfo({
        ...device,
        assignmentInfo: { assigned: false, message: 'Detaylar yüklenemedi' },
      });
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleDeleteClick = (device, e) => {
    e.stopPropagation();
    setDeviceToDelete(device);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;

    setLoading(true);
    try {
      await api.deleteDevice(deviceToDelete.id);

      notifications.show({
        title: 'Başarılı',
        message: 'Cihaz silindi.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      setShowDeleteModal(false);
      setShowDeviceModal(false);
      setDeviceToDelete(null);
      setDeviceModalInfo(null);

      await refreshCurrentDeviceList();
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err?.response?.data?.detail || 'Cihaz silinemedi.',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAssignment = async (e) => {
    e.preventDefault();

    if (!assignForm.employee_id) {
      notifications.show({
        title: 'Uyarı',
        message: 'Lütfen bir personel seçin.',
        color: 'yellow',
      });
      return;
    }

    if (!selectedDevice?.id) {
      notifications.show({
        title: 'Hata',
        message: 'Atanacak cihaz bulunamadı.',
        color: 'red',
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        device_id: selectedDevice.id,
        employee_id: Number(assignForm.employee_id),
        notes: assignForm.notes || '',
      };

      await api.createAssignment(payload);

      notifications.show({
        title: 'Başarılı',
        message: 'Cihaz personele atandı.',
        color: 'teal',
      });

      const updatedAssignmentData = await fetchAndSetAssignment(selectedDevice.id);

      setAssignmentInfo(updatedAssignmentData);
      setDeviceModalInfo((prev) =>
        prev
          ? {
              ...prev,
              status: 'zimmetli',
              assignmentInfo: updatedAssignmentData,
            }
          : prev
      );

      resetAssignmentState();
      setShowAssignmentModal(false);
      setShowDeviceModal(true);

      await refreshCurrentDeviceList();
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err?.response?.data?.detail || 'Atama yapılamadı.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignmentModal = async (device) => {
    setSelectedDevice(device);
    setShowDeviceModal(false);
    setShowAssignmentModal(true);
    setAssignmentLoading(true);
    resetAssignmentState();

    try {
      const assignmentData = await fetchAndSetAssignment(device.id);
      setAssignmentInfo(assignmentData);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleReturnDevice = async (assignmentId) => {
    if (!assignmentId) return;

    setLoading(true);
    try {
      await api.returnDevice(assignmentId);

      notifications.show({
        title: 'Başarılı',
        message: 'Cihaz başarıyla geri alındı.',
        color: 'teal',
      });

      const deviceId = deviceModalInfo?.id || selectedDevice?.id;
      const updatedAssignmentData = await fetchAndSetAssignment(deviceId);

      setDeviceModalInfo((prev) =>
        prev
          ? {
              ...prev,
              status: 'müsait',
              assignmentInfo: updatedAssignmentData,
            }
          : null
      );

      setAssignmentInfo(updatedAssignmentData);

      await refreshCurrentDeviceList();
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err?.response?.data?.detail || 'Cihaz iade işlemi başarısız.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    notifications.show({
      title: 'Bilgi',
      message: 'Rapor indirme işlemi başlatıldı...',
      color: 'blue',
    });
  };

  const breadcrumbsItems = [
    <Button
      key="1"
      variant="subtle"
      size="compact-sm"
      p={0}
      onClick={() => {
        setCurrentView('types');
        setSelectedType(null);
        setSelectedBrand(null);
        setSelectedModel(null);
        setSearchTerm('');
        setSearchResults([]);
      }}
      c={currentView === 'types' ? 'dimmed' : 'blue'}
    >
      Türler
    </Button>,
    selectedType && (
      <Button
        key="2"
        variant="subtle"
        size="compact-sm"
        p={0}
        onClick={() => setCurrentView('brands')}
        c={currentView === 'brands' ? 'dimmed' : 'blue'}
      >
        {selectedType.name}
      </Button>
    ),
    selectedBrand && (
      <Button
        key="3"
        variant="subtle"
        size="compact-sm"
        p={0}
        onClick={() => setCurrentView('models')}
        c={currentView === 'models' ? 'dimmed' : 'blue'}
      >
        {selectedBrand.name}
      </Button>
    ),
    selectedModel && (
      <Text key="4" size="sm" c="dimmed">
        {selectedModel.name}
      </Text>
    ),
  ].filter(Boolean);

  const pageStats = useMemo(() => {
    if (searchTerm) {
      return [
        {
          title: 'Arama Sonucu',
          value: searchResults.length,
          icon: <IconSearch size={18} />,
          color: 'blue',
        },
      ];
    }

    if (currentView === 'types') {
      return [
        {
          title: 'Toplam Tür',
          value: deviceTypes.length,
          icon: <IconClipboardList size={18} />,
          color: 'blue',
        },
      ];
    }

    if (currentView === 'brands') {
      return [
        {
          title: 'Marka Sayısı',
          value: brands.length,
          icon: <IconPackage size={18} />,
          color: 'grape',
        },
      ];
    }

    if (currentView === 'models') {
      return [
        {
          title: 'Model Sayısı',
          value: models.length,
          icon: <IconBox size={18} />,
          color: 'cyan',
        },
      ];
    }

    return [
      {
        title: 'Toplam Cihaz',
        value: devices.length,
        icon: <IconDeviceDesktop size={18} />,
        color: 'teal',
      },
      {
        title: 'Müsait',
        value: devices.filter((d) => d.status?.toLowerCase() === 'müsait').length,
        icon: <IconCheck size={18} />,
        color: 'green',
      },
      {
        title: 'Atanmış',
        value: devices.filter((d) => {
          const status = d.status?.toLowerCase();
          return status === 'atanmış' || status === 'zimmetli';
        }).length,
        icon: <IconUser size={18} />,
        color: 'orange',
      },
      {
        title: 'Bakımda',
        value: devices.filter((d) => {
          const status = d.status?.toLowerCase();
          return status === 'bakım' || status === 'bakımda' || status === 'arıza';
        }).length,
        icon: <IconSettings size={18} />,
        color: 'red',
      },
    ];
  }, [currentView, deviceTypes, brands, models, devices, searchTerm, searchResults]);

  const renderEmptyState = (title, description) => (
    <Paper withBorder radius="xl" p={40}>
      <Stack align="center" gap="sm">
        <ThemeIcon size={60} radius="xl" variant="light" color="gray">
          <IconBox size={28} />
        </ThemeIcon>
        <Title order={4}>{title}</Title>
        <Text c="dimmed" ta="center">
          {description}
        </Text>
      </Stack>
    </Paper>
  );

  const renderTypes = () => {
    if (!deviceTypes.length) {
      return renderEmptyState('Cihaz türü bulunamadı', 'Henüz tanımlanmış cihaz türü yok.');
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
        {deviceTypes.map((type) => (
          <Card
            key={type.id}
            withBorder
            radius="xl"
            p="lg"
            shadow="sm"
            style={{
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              borderColor: '#e9ecef',
            }}
            onClick={() => handleTypeClick(type)}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <ThemeIcon size={56} radius="xl" variant="light" color="blue">
                  {getDeviceIcon(type.name, 28)}
                </ThemeIcon>
                <Badge variant="light" color="blue" size="lg">
                  {type.device_count || 0}
                </Badge>
              </Group>

              <div>
                <Text fw={700} size="lg">
                  {type.name}
                </Text>
                <Text size="sm" c="dimmed" mt={4}>
                  Tür bazlı cihaz kataloğunu görüntüleyin
                </Text>
              </div>

              <Group justify="space-between" mt="xs">
                <Text size="sm" c="dimmed">
                  Detayları aç
                </Text>
                <IconChevronRight size={18} color="gray" />
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    );
  };

  const renderBrands = () => {
    if (!brands.length) {
      return renderEmptyState('Marka bulunamadı', 'Seçilen türe ait marka kaydı bulunamadı.');
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
        {brands.map((brand) => (
          <Card
            key={brand.id}
            withBorder
            radius="xl"
            p="lg"
            shadow="sm"
            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
            onClick={() => handleBrandClick(brand)}
          >
            <Stack gap="md" align="center">
              <ThemeIcon size={64} radius="xl" variant="light" color="grape">
                <Text size="xl" fw={800}>
                  {brand.name?.charAt(0)?.toUpperCase() || '?'}
                </Text>
              </ThemeIcon>

              <Stack gap={4} align="center">
                <Text fw={700} size="lg" ta="center">
                  {brand.name}
                </Text>
                <Badge color="grape" variant="light">
                  {brand.device_count || 0} cihaz
                </Badge>
              </Stack>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    );
  };

  const renderModels = () => {
    if (!models.length) {
      return renderEmptyState('Model bulunamadı', 'Seçilen marka için model kaydı bulunamadı.');
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
        {models.map((model) => (
          <Card
            key={model.id}
            withBorder
            radius="xl"
            p="lg"
            shadow="sm"
            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
            onClick={() => handleModelClick(model)}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <ThemeIcon size={52} radius="xl" variant="light" color="cyan">
                  {getDeviceIcon(model.device_type?.name || selectedType?.name, 24)}
                </ThemeIcon>
                <Badge color="cyan" variant="light">
                  {model.device_count || 0} adet
                </Badge>
              </Group>

              <div>
                <Text fw={700} size="lg">
                  {model.name}
                </Text>
                <Text size="sm" c="dimmed" mt={4}>
                  {model.brand?.name} • {model.device_type?.name}
                </Text>
              </div>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    );
  };

  const renderDevices = (list) => {
    if (!list.length) {
      return renderEmptyState(
        searchTerm ? 'Sonuç bulunamadı' : 'Cihaz bulunamadı',
        searchTerm
          ? 'Arama kriterine uygun cihaz bulunamadı.'
          : 'Seçilen model altında cihaz kaydı bulunamadı.'
      );
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="lg">
        {list.map((device) => (
          <Card
            key={device.id}
            withBorder
            radius="xl"
            p="lg"
            shadow="sm"
            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
            onClick={() => handleDeviceCardClick(device)}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon size={48} radius="xl" variant="light" color="blue">
                    {getDeviceIcon(device.model?.device_type?.name, 22)}
                  </ThemeIcon>
                  <div>
                    <Text fw={700} lineClamp={1}>
                      {device.model?.brand?.name || '-'} {device.model?.name || ''}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {device.model?.device_type?.name || 'Cihaz'}
                    </Text>
                  </div>
                </Group>
                {getStatusBadge(device.status)}
              </Group>

              <Paper radius="lg" p="sm" bg="gray.0">
                <Stack gap={8}>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Barkod
                    </Text>
                    <Text size="sm" fw={700} ff="monospace">
                      {device.barcode || '-'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Seri No
                    </Text>
                    <Text size="sm" ff="monospace">
                      {device.serial_number || '-'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Kondisyon
                    </Text>
                    <Badge color={getConditionColor(device.condition)} variant="light">
                      {device.condition || 'Belirtilmemiş'}
                    </Badge>
                  </Group>
                </Stack>
              </Paper>

              {isAdmin && (
                <Group justify="space-between">
                  <Button
                    variant="light"
                    color="blue"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeviceCardClick(device);
                    }}
                  >
                    Detay
                  </Button>

                  <Button
                    variant="light"
                    color="red"
                    size="sm"
                    onClick={(e) => handleDeleteClick(device, e)}
                  >
                    Sil
                  </Button>
                </Group>
              )}
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          radius="2xl"
          p="xl"
          withBorder
          style={{
            background:
              'linear-gradient(135deg, rgba(34,139,230,0.08) 0%, rgba(151,117,250,0.05) 100%)',
          }}
        >
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                <Group>
                  <Button
                    variant="white"
                    color="dark"
                    radius="xl"
                    onClick={currentView === 'types' ? () => navigate('/inventory') : goBack}
                    leftSection={<IconArrowLeft size={16} />}
                  >
                    {currentView === 'types' ? 'Ana Sayfa' : 'Geri'}
                  </Button>
                </Group>

                <div>
                  <Title order={2} fw={800}>
                    Cihaz Kataloğu
                  </Title>
                  <Text c="dimmed" mt={6}>
                    Cihazları tür, marka ve modele göre kolayca inceleyin. Arama yapın,
                    durumları görün, detaylara hızlıca ulaşın.
                  </Text>
                </div>

                <Breadcrumbs separator="›">{breadcrumbsItems}</Breadcrumbs>
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Paper radius="xl" p="lg" withBorder bg="white">
                <Stack gap="sm">
                  <Text fw={700}>Genel Görünüm</Text>
                  <Group grow>
                    {pageStats.slice(0, 2).map((stat, index) => (
                      <Paper key={index} withBorder radius="lg" p="sm">
                        <Group gap="sm" wrap="nowrap">
                          <ThemeIcon radius="xl" variant="light" color={stat.color}>
                            {stat.icon}
                          </ThemeIcon>
                          <div>
                            <Text size="xs" c="dimmed">
                              {stat.title}
                            </Text>
                            <Text fw={800} size="lg">
                              {stat.value}
                            </Text>
                          </div>
                        </Group>
                      </Paper>
                    ))}
                  </Group>
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        </Paper>

        <Grid>
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Paper withBorder radius="2xl" p="lg">
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon size={42} radius="xl" variant="light" color="blue">
                      <IconSearch size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={700}>Hızlı Cihaz Arama</Text>
                      <Text size="sm" c="dimmed">
                        Seri numarası, barkod, marka veya model ile arayın
                      </Text>
                    </div>
                  </Group>

                  {searchTerm && (
                    <Badge variant="light" color="blue" size="lg">
                      {searchResults.length} sonuç
                    </Badge>
                  )}
                </Group>

                <TextInput
                  placeholder="Örn: SN12345, barkod, Dell, iPhone..."
                  size="lg"
                  radius="xl"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  leftSection={<IconBarcode size={18} stroke={1.5} />}
                  rightSection={searchLoading ? <Loader size="xs" /> : null}
                />
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Paper withBorder radius="2xl" p="lg" h="100%">
              <Stack justify="space-between" h="100%">
                <Group gap="sm">
                  <ThemeIcon size={42} radius="xl" variant="light" color="green">
                    <IconDownload size={20} />
                  </ThemeIcon>
                  <div>
                    <Text fw={700}>Raporlar</Text>
                    <Text size="sm" c="dimmed">
                      Hızlı dışa aktarma işlemleri
                    </Text>
                  </div>
                </Group>

                <Group grow mt="md">
                  <Button
                    variant="light"
                    color="green"
                    radius="xl"
                    leftSection={<IconDownload size={16} />}
                    onClick={() => downloadReport('devices', 'excel')}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="light"
                    color="red"
                    radius="xl"
                    leftSection={<IconFileText size={16} />}
                    onClick={() => downloadReport('devices', 'pdf')}
                  >
                    PDF
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        {!searchTerm && pageStats.length > 1 && (
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            {pageStats.map((stat, index) => (
              <Paper key={index} withBorder radius="xl" p="md">
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">
                      {stat.title}
                    </Text>
                    <Text fw={800} size="xl">
                      {stat.value}
                    </Text>
                  </div>
                  <ThemeIcon size={44} radius="xl" variant="light" color={stat.color}>
                    {stat.icon}
                  </ThemeIcon>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        )}

        <Box pos="relative">
          {loading && (
            <Paper
              radius="xl"
              withBorder
              p="xl"
              mb="lg"
              style={{ borderStyle: 'dashed' }}
            >
              <Center>
                <Stack align="center" gap="sm">
                  <Loader />
                  <Text c="dimmed">Veriler yükleniyor...</Text>
                </Stack>
              </Center>
            </Paper>
          )}

          {!loading && !searchTerm && currentView === 'types' && renderTypes()}
          {!loading && !searchTerm && currentView === 'brands' && renderBrands()}
          {!loading && !searchTerm && currentView === 'models' && renderModels()}
          {!loading && !searchTerm && currentView === 'devices' && renderDevices(devices)}
          {!loading && searchTerm && renderDevices(searchResults)}
        </Box>
      </Stack>

      <Modal
        opened={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
        size="75%"
        radius="2xl"
        padding="xl"
        title={
          <Group gap="sm">
            <ThemeIcon radius="xl" variant="light" color="blue">
              <IconInfoCircle size={18} />
            </ThemeIcon>
            <Text fw={700}>Cihaz Detayı</Text>
          </Group>
        }
      >
        {assignmentLoading ? (
          <Center h={320}>
            <Stack align="center" gap="sm">
              <Loader size="lg" />
              <Text c="dimmed">Cihaz detayları yükleniyor...</Text>
            </Stack>
          </Center>
        ) : deviceModalInfo ? (
          <Stack gap="lg">
            <Paper
              withBorder
              radius="2xl"
              p="lg"
              style={{
                background:
                  'linear-gradient(135deg, rgba(34,139,230,0.05) 0%, rgba(0,0,0,0.01) 100%)',
              }}
            >
              <Grid align="center">
                <Grid.Col span={{ base: 12, md: 2 }}>
                  <Center>
                    <ThemeIcon size={84} radius="xl" variant="light" color="blue">
                      {getDeviceIcon(deviceModalInfo.model?.device_type?.name, 40)}
                    </ThemeIcon>
                  </Center>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Stack gap={6}>
                    <Title order={3}>
                      {deviceModalInfo.model?.brand?.name} {deviceModalInfo.model?.name}
                    </Title>
                    <Text c="dimmed">
                      {deviceModalInfo.model?.device_type?.name} • {deviceModalInfo.serial_number}
                    </Text>

                    <Group mt="xs">
                      {getStatusBadge(deviceModalInfo.status)}
                      <Badge variant="outline" color="gray">
                        {deviceModalInfo.barcode}
                      </Badge>
                    </Group>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Stack align="center" gap="xs">
                    <RingProgress
                      size={110}
                      thickness={10}
                      sections={[
                        {
                          value:
                            deviceModalInfo.condition?.toLowerCase() === 'mükemmel'
                              ? 100
                              : deviceModalInfo.condition?.toLowerCase() === 'iyi'
                              ? 80
                              : deviceModalInfo.condition?.toLowerCase() === 'orta'
                              ? 60
                              : deviceModalInfo.condition?.toLowerCase() === 'zayıf'
                              ? 40
                              : 20,
                          color: getConditionColor(deviceModalInfo.condition),
                        },
                      ]}
                      label={
                        <Center>
                          <Text size="xs" fw={700} ta="center">
                            Kondisyon
                          </Text>
                        </Center>
                      }
                    />
                    <Badge
                      color={getConditionColor(deviceModalInfo.condition)}
                      variant="light"
                      size="lg"
                    >
                      {deviceModalInfo.condition || 'Belirtilmemiş'}
                    </Badge>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Paper>

            <Tabs defaultValue="general" variant="outline" radius="xl">
              <Tabs.List grow>
                <Tabs.Tab value="general" leftSection={<IconInfoCircle size={16} />}>
                  Genel Bilgiler
                </Tabs.Tab>
                <Tabs.Tab value="tech" leftSection={<IconSettings size={16} />}>
                  Teknik Özellikler
                </Tabs.Tab>
                <Tabs.Tab value="assign" leftSection={<IconUser size={16} />}>
                  Atama Durumu
                </Tabs.Tab>
                <Tabs.Tab value="notes" leftSection={<IconNote size={16} />}>
                  Notlar
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="general" pt="xl">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Paper withBorder radius="xl" p="md">
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconCalendar size={18} color="gray" />
                          <Text fw={600}>Satın Alma Tarihi</Text>
                        </Group>
                        <Text>{deviceModalInfo.purchase_date || '-'}</Text>
                      </Group>

                      <Divider />

                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconTrophy size={18} color="gray" />
                          <Text fw={600}>Garanti Bitişi</Text>
                        </Group>
                        <Text>{deviceModalInfo.warranty_expiry || '-'}</Text>
                      </Group>
                    </Stack>
                  </Paper>

                  <Paper withBorder radius="xl" p="md">
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconBarcode size={18} color="gray" />
                          <Text fw={600}>Barkod</Text>
                        </Group>
                        <Text ff="monospace">{deviceModalInfo.barcode || '-'}</Text>
                      </Group>

                      <Divider />

                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconFileText size={18} color="gray" />
                          <Text fw={600}>Seri Numarası</Text>
                        </Group>
                        <Text ff="monospace">{deviceModalInfo.serial_number || '-'}</Text>
                      </Group>
                    </Stack>
                  </Paper>
                </SimpleGrid>
              </Tabs.Panel>

              <Tabs.Panel value="tech" pt="xl">
                {deviceModalInfo.model?.specifications ? (
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                    {Object.entries(deviceModalInfo.model.specifications).map(([key, value]) => (
                      <Paper key={key} withBorder radius="xl" p="md">
                        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
                          {key}
                        </Text>
                        <Text fw={600}>{String(value)}</Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                ) : (
                  renderEmptyState(
                    'Teknik özellik bulunamadı',
                    'Bu model için teknik özellik tanımlanmamış.'
                  )
                )}
              </Tabs.Panel>

              <Tabs.Panel value="assign" pt="xl">
                {deviceModalInfo.assignmentInfo?.assigned ? (
                  <Paper withBorder radius="2xl" p="lg">
                    <Grid align="center">
                      <Grid.Col span={{ base: 12, md: 8 }}>
                        <Stack gap="sm">
                          <Badge color="blue" variant="light" w="fit-content">
                            Aktif Zimmet
                          </Badge>

                          <Title order={4}>
                            {deviceModalInfo.assignmentInfo.assignee?.name || '-'}
                          </Title>

                          <Text size="sm">
                            <b>Departman / Alan:</b>{' '}
                            {deviceModalInfo.assignmentInfo.assignee?.department ||
                              deviceModalInfo.assignmentInfo.assignee?.company ||
                              '-'}
                          </Text>

                          <Text size="sm">
                            <b>E-posta:</b>{' '}
                            {deviceModalInfo.assignmentInfo.assignee?.email || '-'}
                          </Text>

                          <Text size="sm">
                            <b>Telefon:</b>{' '}
                            {deviceModalInfo.assignmentInfo.assignee?.phone || '-'}
                          </Text>

                          <Text size="sm" c="dimmed">
                            Bu cihaz{' '}
                            <b>{deviceModalInfo.assignmentInfo.assigned_date || '-'}</b> tarihinden
                            beri zimmetlidir.
                          </Text>
                        </Stack>
                      </Grid.Col>

                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <Center>
                          <ThemeIcon size={90} radius="xl" variant="light" color="blue">
                            <IconUser size={42} />
                          </ThemeIcon>
                        </Center>
                      </Grid.Col>
                    </Grid>
                  </Paper>
                ) : (
                  <Paper
                    withBorder
                    radius="2xl"
                    p="xl"
                    style={{ borderStyle: 'dashed', textAlign: 'center' }}
                  >
                    <Stack align="center">
                      <ThemeIcon size={70} radius="xl" variant="light" color="gray">
                        <IconUserPlus size={34} />
                      </ThemeIcon>
                      <Text fw={600}>Cihaz şu anda boşta</Text>
                      <Text c="dimmed" size="sm">
                        Herhangi bir personele atanmış değil.
                      </Text>
                    </Stack>
                  </Paper>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="notes" pt="xl">
                <Stack gap="md">
                  <Paper withBorder radius="xl" p="md">
                    <Text fw={700} size="sm" mb="xs">
                      Admin Notları
                    </Text>
                    <Text c={deviceModalInfo.notes ? 'dark' : 'dimmed'} size="sm">
                      {deviceModalInfo.notes || 'Henüz not eklenmemiş.'}
                    </Text>
                  </Paper>

                  <Paper withBorder radius="xl" p="md">
                    <Stack gap="sm">
                      <Text fw={700} size="sm">
                        Hızlı İşlemler
                      </Text>
                      <Group>
                        <Button
                          variant="light"
                          color="blue"
                          radius="xl"
                          leftSection={<IconHistory size={16} />}
                        >
                          Bakım Kaydı Ekle
                        </Button>
                        <Button
                          variant="light"
                          color="gray"
                          radius="xl"
                          leftSection={<IconFileText size={16} />}
                        >
                          Zimmet Belgesi
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                </Stack>
              </Tabs.Panel>
            </Tabs>

            <Divider />

            <Group justify="flex-end">
              {isAdmin && deviceModalInfo.assignmentInfo?.assigned && (
                <Button
                  color="orange"
                  radius="xl"
                  onClick={() =>
                    handleReturnDevice(deviceModalInfo.assignmentInfo.assignment_id)
                  }
                  loading={loading}
                >
                  İade Al
                </Button>
              )}

              {isAdmin && !deviceModalInfo.assignmentInfo?.assigned && (
                <Button
                  color="blue"
                  radius="xl"
                  onClick={() => handleOpenAssignmentModal(deviceModalInfo)}
                >
                  Hızlı Ata
                </Button>
              )}

              <Button variant="default" radius="xl" onClick={() => setShowDeviceModal(false)}>
                Kapat
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>

      <Modal
        opened={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          resetAssignmentState();
        }}
        title={<Text fw={700}>{showAssignForm ? 'Yeni Zimmet Ata' : 'Atama Bilgileri'}</Text>}
        size="lg"
        radius="2xl"
      >
        {showAssignForm ? (
          <form onSubmit={handleSubmitAssignment}>
            <Stack gap="md">
              <Select
                label="Personel Seç"
                placeholder="Kişi arayın..."
                radius="lg"
                data={employees}
                value={assignForm.employee_id}
                onChange={(val) =>
                  setAssignForm((prev) => ({
                    ...prev,
                    employee_id: val || '',
                  }))
                }
                searchable
                required
                nothingFoundMessage="Personel bulunamadı"
              />

              <Textarea
                label="Notlar"
                placeholder="Atama notu..."
                radius="lg"
                minRows={4}
                value={assignForm.notes}
                onChange={(e) => {
                  const value = e.currentTarget.value;

                  setAssignForm((prev) => ({
                    ...prev,
                    notes: value,
                  }));
                }}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  variant="default"
                  radius="xl"
                  onClick={() => {
                    resetAssignmentState();
                    setShowAssignmentModal(false);
                  }}
                >
                  İptal
                </Button>
                <Button type="submit" color="blue" radius="xl" loading={loading}>
                  Kaydet
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="md">
            {assignmentLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : assignmentInfo?.assigned ? (
              <>
                <Badge color="green" size="lg" variant="light" w="fit-content">
                  Atanmış
                </Badge>

                <Text size="sm">
                  <strong>İsim:</strong> {assignmentInfo.assignee?.name || '-'}
                </Text>

                <Text size="sm">
                  <strong>Departman / Alan:</strong>{' '}
                  {assignmentInfo.assignee?.department ||
                    assignmentInfo.assignee?.company ||
                    '-'}
                </Text>

                <Text size="sm">
                  <strong>Tarih:</strong> {assignmentInfo.assigned_date || '-'}
                </Text>

                <Group justify="flex-end" mt="md">
                  {isAdmin && (
                    <Button
                      color="orange"
                      radius="xl"
                      onClick={() => handleReturnDevice(assignmentInfo.assignment_id)}
                      loading={loading}
                    >
                      İade Al
                    </Button>
                  )}
                  <Button
                    variant="default"
                    radius="xl"
                    onClick={() => setShowAssignmentModal(false)}
                  >
                    Kapat
                  </Button>
                </Group>
              </>
            ) : (
              <Center p="xl" style={{ flexDirection: 'column' }}>
                <Text c="dimmed" mb="md">
                  Cihaz boşta.
                </Text>
                {isAdmin && (
                  <Button
                    color="blue"
                    radius="xl"
                    onClick={() => setShowAssignForm(true)}
                    leftSection={<IconUserPlus size={16} />}
                  >
                    Ata
                  </Button>
                )}
              </Center>
            )}
          </Stack>
        )}
      </Modal>

      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={<Text fw={700}>Cihazı Sil</Text>}
        centered
        radius="2xl"
      >
        <Stack gap="lg">
          <Text size="sm">
            <b>{deviceToDelete?.barcode || deviceToDelete?.serial_number || 'Seçili cihaz'}</b>{' '}
            kaydını silmek istediğinize emin misiniz?
          </Text>

          <Group justify="flex-end">
            <Button variant="default" radius="xl" onClick={() => setShowDeleteModal(false)}>
              Vazgeç
            </Button>
            <Button color="red" radius="xl" onClick={handleConfirmDelete} loading={loading}>
              Sil
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default DeviceList;